import type { VesselScopeEntry } from '@/api/common/auth/schema';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Active-vessel context for the HSQE module (spec section 6.2). Sourced ONCE from
// the already-clamped whoami session scope (Vessels[] + VesselScopeKind); the
// frontend never re-derives scope (spec section 5.3 / 6.6). Pattern mirrors
// `src/stores/useLayoutStore.ts` (create<State>()(persist(..., { partialize })))
// with named selectors (wiki: zustand-store-with-selectors).
//
// Persist ONLY `currentVesselId` — scope (scopedVessels + scopeKind) is
// re-hydrated from the session on load to avoid serving a stale clamp.

export type VesselScopeKind = 'fleet-fixed' | 'shore' | 'shipboard';

interface CurrentVesselState {
  currentVesselId: string | null;
  scopeKind: VesselScopeKind | null;
  scopedVessels: VesselScopeEntry[];
  setCurrentVessel: (id: string) => void;
  hydrateFromSession: (scopedVessels: VesselScopeEntry[], scopeKind: VesselScopeKind, currentVesselId: string) => void;
}

export const useCurrentVesselStore = create<CurrentVesselState>()(
  persist(
    (set) => ({
      currentVesselId: null,
      scopeKind: null,
      scopedVessels: [],
      setCurrentVessel: (id) => {
        set(() => ({ currentVesselId: id }));
      },
      hydrateFromSession: (scopedVessels, scopeKind, currentVesselId) => {
        set((state) => {
          // Honour the persisted selection (partialize keeps only currentVesselId)
          // when it is still within the freshly-clamped scope; otherwise fall back
          // to the session's CurrentVesselId. Without this, the persisted value is
          // clobbered on every whoami hydrate and never survives a reload.
          const persistedId = state.currentVesselId;
          const persistedStillInScope =
            persistedId !== null && scopedVessels.some((vessel) => vessel.Id === persistedId);
          return {
            scopedVessels,
            scopeKind,
            currentVesselId: persistedStillInScope ? persistedId : currentVesselId
          };
        });
      }
    }),
    {
      name: 'hsqe-current-vessel-storage',
      // Persist ONLY the active selection; scope re-hydrates from session (section 6.2).
      partialize: (state) => ({ currentVesselId: state.currentVesselId })
    }
  )
);

export const selectCurrentVesselId = (state: CurrentVesselState) => state.currentVesselId;
export const selectScopeKind = (state: CurrentVesselState) => state.scopeKind;
export const selectScopedVessels = (state: CurrentVesselState) => state.scopedVessels;
export const selectSetCurrentVessel = (state: CurrentVesselState) => state.setCurrentVessel;
